/**
 * Equipment-loading limits (transformers, cables, NH fuses) — the loading half of the
 * Ampel table next to voltageConstants.ts (voltage half). Mirrors backend
 * Models/Config/LoadingConstants.cs 1:1; change both together.
 *
 * Two layers, deliberately separate:
 * - Legal: LOADING_COMPLIANCE_LIMIT_PCT = 100 % of the continuous rating I_n
 *   (DIN VDE 0276-1000 cable, DIN EN 60076-7 transformer, NH gG fuse rated current).
 *   The hard cap of every compliance figure, planning verdict and "Überlast" label.
 * - Operational: LOADING_WARNING_PCT = 80 % — the "Gefährdung" reporting threshold
 *   (§ 14a EnWG 15-min peaks, BDEW Ampel Gelb) and the default of the configurable
 *   ThresholdSettings.*.orange level.
 *
 * Rule: instance-tunable colouring (map lines, sidebar cards, grid cells, live alert
 * banners) reads `useSettingsStore(s => s.thresholds)`; regulatory figures (compliance
 * cards, charts with the 80/100 lines, exports, planning verdicts, dashboard health)
 * read these constants. Never write 80 or 100 as a literal in either place.
 */

/** 100 % of I_n / S_n — legal continuous-rating cap. */
export const LOADING_COMPLIANCE_LIMIT_PCT = 100;

/** 80 % — operational warning / "Gefährdung" reporting threshold. */
export const LOADING_WARNING_PCT = 80;

/** Default map colour stages below the warning level (pure UI staging, no norm behind them). */
export const LOADING_DEFAULT_GREEN_PCT = 40;
export const LOADING_DEFAULT_YELLOW_PCT = 60;

export type LoadingSeverity = 'critical' | 'warning' | 'normal';

/**
 * Regulatory Ampel of a loading value: red above the legal cap, yellow above the
 * warning threshold, green otherwise. Use for dashboard/compliance/planning figures;
 * for instance-tunable colouring use the settings-store levels instead.
 */
export function loadingSeverity(pct: number | null | undefined): LoadingSeverity {
  if (pct == null || !Number.isFinite(pct)) return 'normal';
  if (pct > LOADING_COMPLIANCE_LIMIT_PCT) return 'critical';
  if (pct > LOADING_WARNING_PCT) return 'warning';
  return 'normal';
}

/** "80 %" / "100 %" for labels, so texts follow the constants. */
export function pctLabel(pct: number): string {
  return `${pct.toLocaleString('de-DE', { maximumFractionDigits: 1 })} %`;
}

export const LOADING_WARNING_LABEL = pctLabel(LOADING_WARNING_PCT);
export const LOADING_LIMIT_LABEL = pctLabel(LOADING_COMPLIANCE_LIMIT_PCT);

export interface LoadingLevelThresholds {
  green: number;
  yellow: number;
  orange: number;
  red: number;
}

/** Colour of a cable or segment on the map by its loading — one five-step scale for every map layer. */
export function loadingMapColor(pct: number, t: LoadingLevelThresholds): string {
  if (pct >= t.red) return '#ff4444';
  if (pct >= t.orange) return '#ff8800';
  if (pct >= t.yellow) return '#ffee00';
  if (pct >= t.green) return '#44ff44';
  return '#3388ff';
}
